import React from "react";
import { rupiah, formatDate } from "@/lib/format";
import { fileUrl } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

export default function PayslipCard({ detail: d, period, company }) {
  const logoSrc = company?.logo_path ? fileUrl(company.logo_path) : "/logo.png";
  const rows = [
    ["Gaji Pokok", `${d.hari_masuk} hari × ${rupiah(d.gaji_per_hari)}`, d.gaji_pokok],
    ["Uang Makan", `${d.hari_masuk} × ${rupiah(d.uang_makan_per_hari)}`, d.uang_makan],
    ["Uang Luar Kota", `${d.hari_masuk} × ${rupiah(d.uang_luar_kota_per_hari)}`, d.uang_luar_kota],
    ["Uang Lembur", `${d.jam_lembur} jam × ${rupiah(d.tarif_lembur_per_jam)}`, d.uang_lembur],
    ["Total Tunjangan", "", d.total_tunjangan],
  ].filter((r) => r[2] > 0 || r[0] === "Gaji Pokok");

  const potongan = [
    ["Kasbon", d.kasbon],
    ["Potongan Lain", d.potongan_lain],
  ].filter((r) => r[1] > 0);

  return (
    <div className="printable-slip relative overflow-hidden bg-white rounded-xl border border-slate-300 p-5 text-slate-900" style={{ maxWidth: 640 }}>
      <img src={logoSrc} alt="" aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2/3 opacity-[0.04]" />
      {d.status_pembayaran === "SUDAH DIBAYAR" && (
        <div data-testid="slip-lunas-stamp" className="pointer-events-none absolute right-6 top-16 rotate-[-18deg] rounded-md border-[3px] border-green-600 px-3 py-1 text-2xl font-black uppercase tracking-widest text-green-600 opacity-90">
          Lunas
        </div>
      )}
      <div className="flex items-start justify-between border-b-2 border-slate-800 pb-3">
        <div className="flex items-center gap-3">
          <img src={logoSrc} alt="Coating Solution" className="h-12 w-12 rounded-lg object-contain" />
          <div>
            <div className="font-heading text-lg font-extrabold leading-tight">{company?.nama_perusahaan || "CPS PAYROLL"}</div>
            <div className="text-xs text-slate-500">{company?.alamat}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading font-bold">SLIP GAJI</div>
          <div className="text-xs text-slate-500">{period?.nama}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-1 py-3 text-sm">
        <Info l="Periode" v={`${formatDate(period?.tanggal_mulai)} - ${formatDate(period?.tanggal_selesai)}`} />
        <Info l="Status" v={<StatusBadge status={d.status_pembayaran} />} />
        <Info l="Nama" v={d.employee_nama} />
        <Info l="Jabatan" v={d.jabatan || "-"} />
        <Info l="Proyek" v={d.project_nama || "-"} />
        <Info l="Bank" v={`${d.nama_bank || "-"} · ${d.no_rekening || "-"}`} />
      </div>

      {(d.rincian_proyek || []).length > 0 && (
        <div className="mb-3 overflow-x-auto rounded-lg border border-slate-200" data-testid="slip-rincian-proyek">
          <table className="w-full text-xs">
            <thead><tr className="bg-blue-900 text-white">
              <th className="px-2 py-1 text-left">Rincian Proyek</th><th className="px-2 py-1 text-left">Tanggal</th>
              <th className="px-2 py-1 text-right">Hari</th><th className="px-2 py-1 text-right">Lembur</th><th className="px-2 py-1 text-right">Gaji Pokok</th>
            </tr></thead>
            <tbody>{d.rincian_proyek.map((x) => (
              <tr key={x.project_id} className="border-t border-slate-100">
                <td className="px-2 py-1 font-semibold">{x.project_nama}</td><td className="px-2 py-1">{x.tanggal}</td>
                <td className="px-2 py-1 text-right font-mono">{x.hari}</td><td className="px-2 py-1 text-right font-mono">{x.lembur} j</td>
                <td className="px-2 py-1 text-right font-mono">{rupiah(x.gaji)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {(d.rincian_harian || []).length > 0 && <SlipHarian rows={d.rincian_harian} />}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 border-b pb-1 mb-1">Pendapatan</div>
          {rows.map(([l, sub, v], i) => (
            <div key={i} className="flex justify-between py-0.5 text-sm">
              <span>{l}{sub && <span className="block text-[11px] text-slate-400">{sub}</span>}</span>
              <span className="font-mono">{rupiah(v)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t mt-1 pt-1 text-sm font-semibold">
            <span>Total Pendapatan</span><span className="font-mono">{rupiah(d.gaji_kotor)}</span>
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 border-b pb-1 mb-1">Potongan</div>
          {potongan.length ? potongan.map(([l, v], i) => (
            <div key={i} className="flex justify-between py-0.5 text-sm"><span>{l}</span><span className="font-mono text-rose-600">{rupiah(v)}</span></div>
          )) : <div className="text-sm text-slate-400 py-0.5">Tidak ada potongan</div>}
          <div className="flex justify-between border-t mt-1 pt-1 text-sm font-semibold">
            <span>Total Potongan</span><span className="font-mono text-rose-600">{rupiah(d.total_potongan)}</span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-900 px-4 py-2.5 text-white">
        <span className="font-heading font-bold">GAJI BERSIH (THP)</span>
        <span className="font-mono text-xl font-black">{rupiah(d.gaji_bersih)}</span>
      </div>

      {d.projects && d.projects.length > 1 && (
        <div className="mt-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 border-b pb-1 mb-1">Rincian Per Proyek</div>
          {d.projects.map((p, i) => (
            <div key={i} className="flex justify-between py-0.5 text-xs">
              <span>{p.nama} <span className="text-slate-400">({p.hari} hr · {p.lembur} jam lembur)</span></span>
              <span className="font-mono">{rupiah(p.bersih)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid grid-cols-3 gap-4 text-center text-xs text-slate-500">
        <Sign l="Dibuat Oleh" v={company?.penandatangan_admin} />
        <Sign l="Disetujui" v={company?.penandatangan_manager} />
        <Sign l="Diterima Oleh" v={d.employee_nama} />
      </div>
    </div>
  );
}

const SlipHarian = ({ rows }) => {
  const r1 = (v) => Math.round(v * 10) / 10;
  return (
    <div className="mb-3 overflow-x-auto rounded-lg border border-slate-200" data-testid="slip-rincian-harian">
      <div className="bg-orange-500 px-2 py-1 text-xs font-bold text-white">Rekap Harian (Hari Masuk & Lembur)</div>
      <table className="w-full text-xs">
        <thead><tr className="bg-blue-900 text-white">
          <th className="px-2 py-1 text-left">Tanggal</th><th className="px-2 py-1 text-left">Lokasi Proyek</th>
          <th className="px-2 py-1 text-center">Hari Masuk</th><th className="px-2 py-1 text-center">Lembur</th>
        </tr></thead>
        <tbody>{rows.map((x, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="px-2 py-0.5 font-mono whitespace-nowrap">{formatDate(x.tanggal)}</td>
            <td className="px-2 py-0.5">{x.project_nama}{x.susulan && <span className="ml-1 italic text-slate-400">(lembur susulan)</span>}</td>
            <td className="px-2 py-0.5 text-center font-mono">{x.hari}</td><td className="px-2 py-0.5 text-center font-mono">{x.lembur}{x.tunda ? "*" : ""}</td>
          </tr>
        ))}</tbody>
        <tfoot><tr className="bg-orange-50 font-bold" data-testid="slip-rincian-harian-total">
          <td className="px-2 py-1" colSpan={2}>TOTAL (lembur dibayar periode ini)</td>
          <td className="px-2 py-1 text-center font-mono">{r1(rows.reduce((s, x) => s + x.hari, 0))}</td>
          <td className="px-2 py-1 text-center font-mono">{r1(rows.reduce((s, x) => s + (x.tunda ? 0 : x.lembur), 0))}</td>
        </tr></tfoot>
      </table>
      {rows.some((x) => x.tunda) && <div className="px-2 py-1 text-[11px] text-slate-500">* Lembur ini belum dibayar di periode ini (masuk setelah periode dikunci), dibayar di periode berikutnya.</div>}
    </div>
  );
};

const Info = ({ l, v }) => (
  <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">{l}</span><span className="font-medium">{v}</span></div>
);
const Sign = ({ l, v }) => (
  <div><div className="mb-8">{l}</div><div className="border-t border-slate-400 pt-1 font-medium text-slate-700">{v || "________"}</div></div>
);
